// src/features/auth/utils/permission.utils.ts

/**
 * Check if user has a specific role
 */
export function hasRole(userRoles: string[], role: string): boolean {
  return userRoles.includes(role);
}

/**
 * Check if user has any of the specified roles
 */
export function hasAnyRole(userRoles: string[], roles: string[]): boolean {
  return roles.some(role => userRoles.includes(role));
}

/**
 * Check if user has all specified roles
 */
export function hasAllRoles(userRoles: string[], roles: string[]): boolean {
  return roles.every(role => userRoles.includes(role));
}

/**
 * Check if user has a specific permission
 */
export function hasPermission(userPermissions: string[], permission: string): boolean {
  // Direct permission check
  if (userPermissions.includes(permission)) {
    return true;
  }

  // Wildcard permission check (e.g., 'users:*' grants all user permissions)
  const [resource, action] = permission.split(':');
  if (resource && userPermissions.includes(`${resource}:*`)) {
    return true;
  }

  // Super admin wildcard
  if (userPermissions.includes('*:*') || userPermissions.includes('admin:*')) {
    return true;
  }

  return false;
}

/**
 * Check if user has any of the specified permissions
 */
export function hasAnyPermission(userPermissions: string[], permissions: string[]): boolean {
  return permissions.some(permission => hasPermission(userPermissions, permission));
}

/**
 * Check if user has all specified permissions
 */
export function hasAllPermissions(userPermissions: string[], permissions: string[]): boolean {
  return permissions.every(permission => hasPermission(userPermissions, permission));
}

/**
 * Get resource permissions (CRUD operations)
 */
export function getResourcePermissions(
  userPermissions: string[],
  resource: string
): {
  canView: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canManage: boolean;
} {
  return {
    canView: hasPermission(userPermissions, `${resource}:view`),
    canCreate: hasPermission(userPermissions, `${resource}:create`),
    canUpdate: hasPermission(userPermissions, `${resource}:update`),
    canDelete: hasPermission(userPermissions, `${resource}:delete`),
    canManage: hasPermission(userPermissions, `${resource}:manage`),
  };
}

/**
 * Filter permissions by resource
 */
export function filterPermissionsByResource(
  permissions: string[],
  resource: string
): string[] {
  return permissions.filter(permission => permission.startsWith(`${resource}:`));
}

/**
 * Get unique resources from permissions
 */
export function getResourcesFromPermissions(permissions: string[]): string[] {
  const resources = new Set<string>();
  
  permissions.forEach(permission => {
    const [resource] = permission.split(':');
    if (resource) {
      resources.add(resource);
    }
  });
  
  return Array.from(resources);
}

/**
 * Check if permission grants access to action
 */
export function permissionGrantsAction(
  permission: string,
  targetResource: string,
  targetAction: string
): boolean {
  const [resource, action] = permission.split(':');
  
  // Check exact match
  if (resource === targetResource && action === targetAction) {
    return true;
  }
  
  // Check wildcard resource
  if (resource === '*' || (resource === targetResource && action === '*')) {
    return true;
  }
  
  return false;
}

/**
 * Merge permissions from multiple sources (roles, direct permissions, etc.)
 */
export function mergePermissions(...permissionSets: string[][]): string[] {
  const merged = new Set<string>();
  
  permissionSets.forEach(permissions => {
    permissions.forEach(permission => {
      merged.add(permission);
    });
  });
  
  return Array.from(merged);
}

/**
 * Check if user is admin (has admin role or admin permissions)
 */
export function isAdmin(roles: string[], permissions: string[]): boolean {
  return (
    hasRole(roles, 'admin') ||
    hasRole(roles, 'super_admin') ||
    hasPermission(permissions, 'admin:*') ||
    hasPermission(permissions, '*:*')
  );
}

/**
 * Get permission hierarchy level
 * Higher level = more permissions
 */
export function getPermissionLevel(permission: string): number {
  if (permission === '*:*') return 100;
  if (permission.endsWith(':*')) return 50;
  if (permission.includes(':manage')) return 40;
  if (permission.includes(':delete')) return 30;
  if (permission.includes(':update')) return 20;
  if (permission.includes(':create')) return 10;
  if (permission.includes(':view')) return 5;
  return 0;
}

/**
 * Sort permissions by hierarchy level
 */
export function sortPermissionsByLevel(permissions: string[]): string[] {
  return [...permissions].sort((a, b) => {
    return getPermissionLevel(b) - getPermissionLevel(a);
  });
}